import { useConsolidatedSubscription } from "@mirage/shared/hooks/useConsolidatedSubscription";
import { getCombineAsyncRequestsFunc } from "@mirage/shared/util/combine-async-requests";

import { atom, useAtom } from "jotai";
import { useCallback, useEffect } from "react";
import {
  getAllOnboardingValues,
  getLatestWelcomeModalComplete,
  getStacksOnboardingState,
  isUsersDay0,
  markDashWelcomeModalCompleteIfNecessary,
  setOnboardingValue,
  setOnboardingValue as setOnboardingValueService,
  setStacksOnboardingState,
  subscribeToOnboardingValues,
} from ".";

import type { OnboardingStorage, StacksOnboardingState } from "./service";

const onboardingValuesAtom = atom<OnboardingStorage | undefined>(undefined);

// We only need to fetch the initial values once.
const getAllOnboardingValuesCombined = getCombineAsyncRequestsFunc(
  getAllOnboardingValues,
);

export function useOnboardingValues(): {
  onboardingValues: OnboardingStorage | undefined;
  // flag to determine if the onboarding values have been loaded or not
  // since !onboardingValues?.firstLoginTimeWeb will be incorrectly be true if
  // we haven't yet loaded these values from storage etc.
  hasLoadedOnboardingValues: boolean;
  getOnboardingValue: <K extends keyof OnboardingStorage>(
    key: K,
  ) => Promise<OnboardingStorage[K]>;
  setOnboardingValue: <K extends keyof OnboardingStorage>(
    key: K,
    value: OnboardingStorage[K],
  ) => Promise<void>;
} {
  const [onboardingValues, setOnboardingValues] = useAtom(onboardingValuesAtom);

  useConsolidatedSubscription({
    hookName: "useOnboardingValues",
    setState: setOnboardingValues,
    getInitialData: getAllOnboardingValuesCombined,
    subscribe: subscribeToOnboardingValues,
  });

  const getOnboardingValue = useCallback(
    async <K extends keyof OnboardingStorage>(
      key: K,
    ): Promise<OnboardingStorage[K]> => {
      const values =
        onboardingValues ?? (await getAllOnboardingValuesCombined());
      return values[key];
    },
    [onboardingValues],
  );

  const setOnboardingValue = useCallback(
    async <K extends keyof OnboardingStorage>(
      key: K,
      value: OnboardingStorage[K],
    ): Promise<void> => {
      setOnboardingValues({
        ...(onboardingValues ?? (await getAllOnboardingValuesCombined())),
        [key]: value,
      });
      setOnboardingValueService(key, value);
    },
    [onboardingValues, setOnboardingValues],
  );

  const hasLoadedOnboardingValues = onboardingValues !== undefined;

  return {
    onboardingValues,
    hasLoadedOnboardingValues,
    getOnboardingValue,
    setOnboardingValue,
  };
}

export const isUserDay0Atom = atom<boolean>(false);

export function useIsUsersDay0() {
  const [isDay0, setIsUsersDay0] = useAtom(isUserDay0Atom);

  useEffect(() => {
    const doSetDay0 = async () => {
      const value = await isUsersDay0();
      setIsUsersDay0(value);
    };
    doSetDay0();
  }, [setIsUsersDay0]);
  return isDay0;
}

export const welcomeModalDismissedAtom = atom<boolean>(true);
export const welcomeModalInitializedAtom = atom<boolean>(false);

export function useWelcomeModalDismissed(modalVersion: number) {
  const [dismissed, setDismissed] = useAtom(welcomeModalDismissedAtom);
  const [initialized, setInitialized] = useAtom(welcomeModalInitializedAtom);

  useEffect(() => {
    const doSetDismissed = async () => {
      const value = await getLatestWelcomeModalComplete();
      setInitialized(true);
      setDismissed(!!value && value >= modalVersion);
    };
    doSetDismissed();
  }, [modalVersion, setDismissed, setInitialized]);

  const handleDismissed = async () => {
    setDismissed(true);
    await markDashWelcomeModalCompleteIfNecessary(modalVersion);
    setOnboardingValue("latestDismissedWelcomeModal", modalVersion);
  };

  return { dismissed, setDismissed: handleDismissed, initialized };
}

const stacksOnboardingStateAtom = atom<StacksOnboardingState>();

export function useStacksOnboardingState() {
  const [onboardingState, setOnboardingState] = useAtom(
    stacksOnboardingStateAtom,
  );

  useEffect(() => {
    const applyOnboardingState = async () => {
      const state = await getStacksOnboardingState();
      setOnboardingState(state || {});
    };
    void applyOnboardingState();
  }, [setOnboardingState]);

  const updateOnboardingState = useCallback(
    (updates: StacksOnboardingState) => {
      const newState: StacksOnboardingState = {
        ...onboardingState,
        ...updates,
      };
      setOnboardingState(newState);

      // Persist in local storage and in server-side UserMetadata
      setStacksOnboardingState(newState);
    },
    [onboardingState, setOnboardingState],
  );

  return { onboardingState, updateOnboardingState };
}
